import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Button, Typography, useTheme } from '@mui/material'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import LogoutIcon from '@mui/icons-material/Logout'

import { AppRoutes } from '../../helpers/Routes'
import { TenantInspectionHubMoveOut } from '../../types/inspection'
import { LifecycleSection, InspectionEmptyState } from './InspectionSectionCard'
import { InspectionSummaryCard } from './InspectionSummaryCard'
import {
    countAreas,
    damagesToPreviewItems,
    formatDamageTotal,
    getLatestCompletedDate,
} from './inspectionUtils'
import { InspectionChipStatus } from './InspectionStatusChip'

interface MoveOutSectionProps {
    moveOut: TenantInspectionHubMoveOut
    defaultExpanded?: boolean
}

export const MoveOutSection: React.FC<MoveOutSectionProps> = ({
    moveOut,
    defaultExpanded = false,
}) => {
    const navigate = useNavigate()
    const theme = useTheme()

    const handleViewResults = () => {
        navigate(AppRoutes.inspectionMoveOut)
    }

    const getSectionStatus = (): InspectionChipStatus => {
        if (moveOut.state === 'complete') return 'complete'
        if (moveOut.state === 'pending' || moveOut.state === 'processing') {
            return 'pending'
        }
        return 'not_available'
    }

    const getSummary = () => {
        if (moveOut.state === 'not_available') return 'Not available yet'
        if (moveOut.state === 'pending' || moveOut.state === 'processing') {
            return 'Inspection in progress'
        }
        const damageCount = moveOut.damages.length
        if (damageCount > 0) {
            return `Complete · ${damageCount} damage${damageCount === 1 ? '' : 's'} · ${formatDamageTotal(moveOut.total_damages)} total`
        }
        return `Complete · No damages recorded`
    }

    const previewItems = damagesToPreviewItems(moveOut.damages ?? [])
    const areaCount = countAreas(moveOut.detail)
    const completedDate = getLatestCompletedDate(moveOut.detail)

    return (
        <LifecycleSection
            sectionNumber={3}
            title="Move-Out"
            icon={<LogoutIcon />}
            status={getSectionStatus()}
            summary={getSummary()}
            defaultExpanded={defaultExpanded}
        >
            {moveOut.state === 'not_available' && (
                <InspectionEmptyState message="Not available yet" />
            )}

            {(moveOut.state === 'pending' || moveOut.state === 'processing') && (
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Move-out inspection in progress. Your property manager is
                    conducting the final walk-through.
                </Typography>
            )}

            {moveOut.state === 'complete' && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <InspectionSummaryCard
                        icon={<LogoutIcon sx={{ fontSize: 22 }} />}
                        title="Move-Out Inspection"
                        status="complete"
                        completedDate={completedDate}
                        areaCount={areaCount}
                        previewItems={previewItems}
                        previewVariant="damages"
                        viewResultsLabel="View inspection results"
                        onViewResults={handleViewResults}
                    />
                    {moveOut.fmo_available && (
                        <Button
                            variant="contained"
                            fullWidth
                            startIcon={<ExitToAppIcon />}
                            onClick={() => navigate(AppRoutes.moveOut)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 600,
                                boxShadow: `0 4px 15px ${theme.palette.primary.main}40`,
                            }}
                        >
                            View move-out statement
                        </Button>
                    )}
                </Box>
            )}
        </LifecycleSection>
    )
}
